"use client";

import { useQueryClient } from "@tanstack/react-query";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import { invalidatePostedTuitionLedgerQueries } from "@/features/tuition/hooks/invalidate-posted-tuition-ledger-queries";
import { useCalculationCycleCurrent } from "@/features/tuition/hooks/useCalculationCycleCurrent";
import { useTuitionCalculationCompletionToast } from "@/features/tuition/hooks/useTuitionCalculationCompletionToast";
import {
  useActiveTuitionCalculationCycleId,
  useTuitionWorkflowSession,
} from "@/features/tuition/hooks/useTuitionWorkflowSession";
import {
  shouldFetchCurrentCalculationCycle,
  writeTuitionWorkflowSession,
} from "@/features/tuition/utils/tuition-workflow-session";
import { CalculationCycleStatus } from "@/openapi/generated";

export function TuitionCalculationWatcher() {
  const pathname = usePathname();
  const queryClient = useQueryClient();
  const isAdminDashboard = pathname?.startsWith("/admin") ?? false;
  const activeCycleId = useActiveTuitionCalculationCycleId();
  const shouldTrack = shouldFetchCurrentCalculationCycle(
    isAdminDashboard,
    activeCycleId,
  );
  const [pollActive, setPollActive] = useState(false);
  const session = useTuitionWorkflowSession(shouldTrack ? activeCycleId : null);
  const isAwaitingCompletion = Boolean(session?.calculationStarted);
  const isAwaitingPost = Boolean(session?.postAccepted);

  const {
    data: cycle,
    isPending,
    isSuccess,
  } = useCalculationCycleCurrent({
    enabled: shouldTrack,
    poll: pollActive,
    pollWhilePendingPost: isAwaitingPost,
  });

  const isTerminalStatus =
    cycle?.status === CalculationCycleStatus.Calculated ||
    cycle?.status === CalculationCycleStatus.CalculationFailed;
  const isPostTerminal =
    cycle?.status === CalculationCycleStatus.Synced ||
    cycle?.status === CalculationCycleStatus.SyncFailed;

  useEffect(() => {
    if (!shouldTrack) {
      setPollActive(false);
      return;
    }

    setPollActive(
      (isAwaitingCompletion && !isTerminalStatus) ||
        (isAwaitingPost && !isPostTerminal),
    );
  }, [
    isAwaitingCompletion,
    isAwaitingPost,
    isPostTerminal,
    isTerminalStatus,
    shouldTrack,
  ]);

  useTuitionCalculationCompletionToast({
    cycle,
    tracking: shouldTrack && isAwaitingCompletion,
  });

  useEffect(() => {
    if (!shouldTrack || activeCycleId == null) return;
    if (!isAwaitingCompletion || !isTerminalStatus) return;

    writeTuitionWorkflowSession(activeCycleId, { calculationStarted: false });
  }, [activeCycleId, isAwaitingCompletion, isTerminalStatus, shouldTrack]);

  useEffect(() => {
    if (!shouldTrack || activeCycleId == null || !isAwaitingPost) return;

    if (cycle?.status === CalculationCycleStatus.Synced) {
      void invalidatePostedTuitionLedgerQueries(queryClient);
      writeTuitionWorkflowSession(activeCycleId, { postAccepted: false });
      return;
    }

    if (cycle?.status === CalculationCycleStatus.SyncFailed) {
      writeTuitionWorkflowSession(activeCycleId, { postAccepted: false });
    }
  }, [activeCycleId, cycle?.status, isAwaitingPost, queryClient, shouldTrack]);

  useEffect(() => {
    if (!shouldTrack || activeCycleId == null) return;
    if (!isSuccess || isPending || cycle != null) return;

    writeTuitionWorkflowSession(activeCycleId, { calculationStarted: false });
  }, [activeCycleId, cycle, isPending, isSuccess, shouldTrack]);

  return null;
}
